import { create } from 'zustand';
import type { ChatMessage, CuratedState } from '@citiguide/shared';
import { getURLParams, writeCuratedStateToURL, writeOpenPlanToURL, MAX_PINS } from '../utils/urlParams';
import { chatTranscriptKey } from '../utils/storageScope';

export type { CuratedState };

/** Transcript entries kept in the tab (Decision xKYl8XgX); the server only ever sees the last 8. */
export const MAX_TRANSCRIPT_MESSAGES = 20;

/** The plan the visitor currently has open (from My plans or a shared link). */
export interface OpenPlan {
  id?: string;
  name: string;
}

export const EMPTY_STATE: CuratedState = { categories: [], audience: [], price: null, kind: null, near: null };

interface CurationStore {
  /** What the visitor has chosen to see: the single source for chips, the URL and the marker query. */
  state: CuratedState;
  /** Chat transcript, mirrored to sessionStorage (per tab, never localStorage, never in a link). */
  messages: ChatMessage[];
  pinnedMarkerIds: string[];
  openPlan: OpenPlan | null;
  setCategories: (ids: string[]) => void;
  setAudience: (ids: string[]) => void;
  setPrice: (price: CuratedState['price']) => void;
  setKind: (kind: CuratedState['kind']) => void;
  setNear: (near: CuratedState['near']) => void;
  /** Replace the whole filter (the chat's state is the full desired filter, never a delta). */
  applyServerState: (state: CuratedState) => void;
  clearFilters: () => void;
  appendMessage: (message: ChatMessage) => void;
  clearTranscript: () => void;
  togglePin: (markerId: string) => void;
  /** Open a saved or shared plan: filters, pins and the open-plan marker move together. */
  openSavedPlan: (plan: { id?: string; name: string; state: CuratedState; pinnedMarkerIds: string[] }) => void;
  closePlan: () => void;
  /** Re-read the URL (later navigation); no-op when it already matches. */
  hydrateFromURL: () => void;
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

export const sameState = (a: CuratedState, b: CuratedState): boolean =>
  sameList(a.categories, b.categories) &&
  sameList(a.audience, b.audience) &&
  a.price === b.price &&
  a.kind === b.kind &&
  (a.near?.radius_m ?? null) === (b.near?.radius_m ?? null);

/** Read the initial state straight from the URL so the very first marker request is already filtered. */
const readStateFromURL = (): CuratedState => {
  const params = getURLParams();
  return {
    categories: params.categories,
    audience: params.audience,
    price: params.price,
    kind: params.kind,
    near: params.nearRadius ? { radius_m: params.nearRadius } : null,
  };
};

const readOpenPlanFromURL = (): { openPlan: OpenPlan | null; pins: string[] } => {
  const params = getURLParams();
  if (params.pins.length === 0 && !params.planName) return { openPlan: null, pins: [] };
  return { openPlan: { name: params.planName ?? '' }, pins: params.pins };
};

const isChatMessage = (m: unknown): m is ChatMessage => {
  const x = m as Partial<ChatMessage> | null;
  return !!x && (x.role === 'user' || x.role === 'assistant') && typeof x.content === 'string';
};

/** Defensive: corrupt or foreign data in sessionStorage yields an empty transcript, never a throw. */
export const readTranscript = (): ChatMessage[] => {
  try {
    const raw = window.sessionStorage.getItem(chatTranscriptKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isChatMessage).slice(-MAX_TRANSCRIPT_MESSAGES) : [];
  } catch {
    return [];
  }
};

const writeTranscript = (messages: ChatMessage[]) => {
  try {
    if (messages.length === 0) window.sessionStorage.removeItem(chatTranscriptKey());
    else window.sessionStorage.setItem(chatTranscriptKey(), JSON.stringify(messages));
  } catch {
    // storage full or blocked: the in-memory transcript still works
  }
};

export const useCurationStore = create<CurationStore>((set, get) => {
  const apply = (next: CuratedState) => {
    writeCuratedStateToURL(next);
    set({ state: next });
  };
  const initialPlan = readOpenPlanFromURL();
  return {
    state: readStateFromURL(),
    messages: readTranscript(),
    pinnedMarkerIds: initialPlan.pins,
    openPlan: initialPlan.openPlan,
    setCategories: (ids) => apply({ ...get().state, categories: ids }),
    setAudience: (ids) => apply({ ...get().state, audience: ids }),
    setPrice: (price) => apply({ ...get().state, price }),
    setKind: (kind) => apply({ ...get().state, kind }),
    setNear: (near) => apply({ ...get().state, near }),
    applyServerState: (state) =>
      apply({
        categories: [...state.categories],
        audience: [...state.audience],
        price: state.price ?? null,
        kind: state.kind ?? null,
        near: state.near ? { radius_m: state.near.radius_m } : null,
      }),
    clearFilters: () => apply({ ...EMPTY_STATE }),
    appendMessage: (message) => {
      const messages = [...get().messages, message].slice(-MAX_TRANSCRIPT_MESSAGES);
      writeTranscript(messages);
      set({ messages });
    },
    clearTranscript: () => {
      writeTranscript([]);
      set({ messages: [] });
    },
    togglePin: (markerId) => {
      const cur = get().pinnedMarkerIds;
      let next: string[];
      if (cur.includes(markerId)) next = cur.filter((id) => id !== markerId);
      else if (cur.length >= MAX_PINS) return;
      else next = [...cur, markerId];
      set({ pinnedMarkerIds: next });
      const plan = get().openPlan;
      if (plan) writeOpenPlanToURL({ name: plan.name, pinnedMarkerIds: next });
    },
    openSavedPlan: (plan) => {
      const pins = plan.pinnedMarkerIds.slice(0, MAX_PINS);
      get().applyServerState(plan.state);
      writeOpenPlanToURL({ name: plan.name, pinnedMarkerIds: pins });
      set({ pinnedMarkerIds: pins, openPlan: { id: plan.id, name: plan.name } });
    },
    closePlan: () => {
      writeOpenPlanToURL(null);
      set({ openPlan: null });
    },
    hydrateFromURL: () => {
      const next = readStateFromURL();
      if (!sameState(next, get().state)) set({ state: next });
    },
  };
});

export const hasActiveFilters = (state: CuratedState): boolean =>
  state.categories.length > 0 ||
  state.audience.length > 0 ||
  state.price !== null ||
  state.kind !== null ||
  state.near !== null;

/** Coordinates are rounded to 3 decimals (~100 m) so position updates do not churn the react-query key. */
export const formatNearParam = (radius: number, location: readonly [number, number]): string =>
  `${location[0].toFixed(3)},${location[1].toFixed(3)},${radius}`;

/**
 * Query params for GET /api/markers. Ids are sorted so the react-query key is stable.
 * `near` is sent only when the visitor has granted location (a location exists).
 */
export const toMarkerQueryParams = (
  state: CuratedState,
  location?: readonly [number, number] | null,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (state.categories.length > 0) params.categories = [...state.categories].sort().join(',');
  if (state.audience.length > 0) params.audience = [...state.audience].sort().join(',');
  if (state.price) params.price = state.price;
  if (state.kind) params.kind = state.kind;
  if (state.near && location) params.near = formatNearParam(state.near.radius_m, location);
  return params;
};
